// frontend/src/components/CommentsSection.js
// W2 — Comments, one-level replies, pinning, report & delete on a Look.
// Authenticated users can comment; guests see read-only + a login link.

import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from "react";
import {
  FaRegComment,
  FaReply,
  FaFlag,
  FaTrash,
  FaThumbtack,
} from "react-icons/fa";
import AuthRequiredModal from "./AuthRequiredModal";

const C = {
  text: "#0f172a",
  muted: "#6b7280",
  faint: "#64748b",
  blue: "#1d4f80",
  link: "#2563eb",
  border: "#edf1f6",
};

function timeAgo(dateStr) {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "";
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function initialsOf(name) {
  return String(name || "U")
    .split(" ")
    .map((s) => s[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function Avatar({ name, photo, size = 34 }) {
  if (photo) {
    return (
      <img
        src={photo}
        alt={name}
        style={{
          width: size,
          height: size,
          borderRadius: "50%",
          objectFit: "cover",
          flexShrink: 0,
        }}
      />
    );
  }
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: C.blue,
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: size * 0.42,
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      {initialsOf(name)}
    </div>
  );
}

function CommentActions({ comment, canInteract, onReply, onReport, onDelete, onPin }) {
  const canModerate = comment.isOwner || comment.isAdmin;
  const buttons = [];
  if (canModerate) {
    buttons.push(
      <button
        key="pin"
        onClick={() => onPin(comment)}
        style={actionBtnStyle(comment.pinned ? "#059669" : C.faint)}
        title={comment.pinned ? "Unpin" : "Pin"}
      >
        <FaThumbtack /> {comment.pinned ? "Pinned" : "Pin"}
      </button>,
    );
  }
  if (canInteract) {
    buttons.push(
      <button key="reply" onClick={() => onReply(comment)} style={actionBtnStyle(C.faint)} title="Reply">
        <FaReply /> Reply
      </button>,
    );
    if (!comment.isAuthor && !comment.isOwner) {
      buttons.push(
        <button key="report" onClick={() => onReport(comment)} style={actionBtnStyle("#b45309")} title="Report">
          <FaFlag /> Report
        </button>,
      );
    }
  }
  if (comment.isAuthor || comment.isOwner || comment.isAdmin) {
    buttons.push(
      <button key="delete" onClick={() => onDelete(comment)} style={actionBtnStyle("#b91c1c")} title="Delete">
        <FaTrash /> Delete
      </button>,
    );
  }
  return <div style={{ display: "flex", gap: "14px", marginTop: "6px", flexWrap: "wrap" }}>{buttons}</div>;
}

const actionBtnStyle = (color) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: "5px",
  border: "none",
  background: "transparent",
  color,
  fontSize: "12px",
  fontWeight: 600,
  cursor: "pointer",
  padding: "2px 0",
});

const CommentsSection = forwardRef(function CommentsSection(
  { lookId, apiBase, isMobile = false, containerStyle = {} },
  ref,
) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [newText, setNewText] = useState("");
  const [replyToId, setReplyToId] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [sheetOpen, setSheetOpen] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [authTick, setAuthTick] = useState(0);

  // Expose a way for the Look page's comment icon to open the bottom sheet.
  useImperativeHandle(ref, () => ({
    openSheet: () => setSheetOpen(true),
  }));

  const token = localStorage.getItem("ogestyle_token");
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}/api/looks/${lookId}/comments`, {
        headers: authHeaders,
      });
      const data = await res.json();
      if (res.ok) setComments(data.comments || []);
    } catch (err) {
      setError("Unable to load comments");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookId, apiBase]);

  useEffect(() => {
    load();
  }, [load]);

  const flash = (msg) => {
    setNotice(msg);
    setTimeout(() => setNotice(""), 3200);
  };

  const submit = async (body, parentId) => {
    if (!body.trim()) return;
    setBusy(true);
    try {
      const res = await fetch(`${apiBase}/api/looks/${lookId}/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders },
        body: JSON.stringify({ body, parentId: parentId || undefined }),
      });
      const data = await res.json();
      if (!res.ok) {
        flash(data?.message || "Unable to post comment");
        return;
      }
      if (parentId) {
        setComments((prev) =>
          prev.map((c) =>
            c._id === parentId ? { ...c, replies: [...(c.replies || []), data.comment] } : c,
          ),
        );
        setReplyToId(null);
        setReplyText("");
      } else {
        setComments((prev) => [data.comment, ...prev]);
        setNewText("");
      }
      if (data.pending) flash("Thanks! Your comment is pending review.");
    } catch (err) {
      flash("Unable to post comment");
    } finally {
      setBusy(false);
    }
  };

  const removeComment = async (comment) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      const res = await fetch(`${apiBase}/api/looks/${lookId}/comments/${comment._id}`, {
        method: "DELETE",
        headers: authHeaders,
      });
      if (res.ok) {
        setComments((prev) =>
          prev
            .filter((c) => c._id !== comment._id)
            .map((c) => ({
              ...c,
              replies: (c.replies || []).filter((r) => r._id !== comment._id),
            })),
        );
      }
    } catch (err) {
      // ignore
    }
  };

  const reportComment = async (comment) => {
    const reason = window.prompt("Why are you reporting this comment?");
    if (!reason || !reason.trim()) return;
    try {
      const res = await fetch(`${apiBase}/api/looks/${lookId}/comments/${comment._id}/report`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders },
        body: JSON.stringify({ reason: reason.trim() }),
      });
      if (res.ok) flash("Reported — thanks for keeping Looks positive.");
      else flash("Unable to report");
    } catch (err) {
      flash("Unable to report");
    }
  };

  const pinComment = async (comment) => {
    try {
      const res = await fetch(`${apiBase}/api/looks/${lookId}/comments/${comment._id}/pin`, {
        method: "POST",
        headers: authHeaders,
      });
      const data = await res.json();
      if (res.ok) {
        setComments((prev) => {
          const updated = prev
            .map((c) => (c._id === comment._id ? { ...c, pinned: data.pinned } : { ...c, pinned: false }))
            .sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0));
          return updated;
        });
      }
    } catch (err) {
      // ignore
    }
  };

  const renderComment = (c, isReply) => {
    const removed = c.removed || c.status === "deleted" || c.status === "hidden";
    return (
      <div
        key={c._id}
        style={{
          display: "flex",
          gap: "10px",
          padding: isReply ? "10px 0 0 30px" : "14px 0",
          borderTop: isReply ? "1px dashed #f0f3f7" : `1px solid ${C.border}`,
        }}
      >
        <Avatar name={c.authorName} photo={c.authorPhoto} size={isReply ? 28 : 34} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
            <span style={{ fontSize: "13px", fontWeight: 700, color: C.text }}>{c.authorName}</span>
            {c.pinned && (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  fontSize: "10px",
                  fontWeight: 700,
                  color: "#fff",
                  background: "#059669",
                  borderRadius: "999px",
                  padding: "2px 8px",
                }}
              >
                <FaThumbtack size={9} /> Pinned
              </span>
            )}
            {c.pending && (
              <span
                style={{
                  fontSize: "10px",
                  fontWeight: 700,
                  color: "#b45309",
                  background: "#fffbeb",
                  borderRadius: "999px",
                  padding: "2px 8px",
                }}
              >
                Awaiting review
              </span>
            )}
            <span style={{ fontSize: "11px", color: "#9ca3af" }}>{timeAgo(c.createdAt)}</span>
          </div>
          <div style={{ fontSize: "14px", color: C.text, lineHeight: 1.5, marginTop: "3px", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {removed ? <span style={{ color: "#9ca3af", fontStyle: "italic" }}>Comment removed</span> : c.body}
          </div>
          {!removed && (
            <CommentActions
              comment={c}
              canInteract={!!token}
              onReply={() => setReplyToId((prev) => (prev === c._id ? null : c._id))}
              onReport={reportComment}
              onDelete={removeComment}
              onPin={pinComment}
            />
          )}
          {replyToId === c._id && !removed && (
            <div style={{ display: "flex", gap: "8px", marginTop: "8px", alignItems: "flex-start" }}>
              <input
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    submit(replyText, c._id);
                  }
                }}
                placeholder="Write a reply…"
                maxLength={1000}
                style={{
                  flex: 1,
                  border: `1px solid ${C.border}`,
                  borderRadius: "10px",
                  padding: "9px 12px",
                  fontSize: "13px",
                  outline: "none",
                }}
              />
              <button
                onClick={() => submit(replyText, c._id)}
                disabled={busy || !replyText.trim()}
                style={{
                  border: "none",
                  borderRadius: "10px",
                  background: "linear-gradient(135deg, #f59e0b 0%, #f97316 100%)",
                  color: "#fff",
                  padding: "9px 14px",
                  fontSize: "13px",
                  fontWeight: 700,
                  cursor: busy || !replyText.trim() ? "default" : "pointer",
                  opacity: busy || !replyText.trim() ? 0.6 : 1,
                }}
              >
                Reply
              </button>
            </div>
          )}
          {c.replies && c.replies.length > 0 && c.replies.map((r) => renderComment(r, true))}
        </div>
      </div>
    );
  };

  const totalCount = comments.reduce((acc, c) => acc + 1 + (c.replies?.length || 0), 0);

  // The composer + full thread — shared by the desktop inline section and the
  // mobile bottom sheet so they never drift apart.
  const renderThread = () => (
    <>
      {notice && (
        <div
          style={{
            background: "#eff6ff",
            border: "1px solid #bfdbfe",
            color: "#1d4f80",
            borderRadius: "10px",
            padding: "10px 14px",
            fontSize: "13px",
            marginBottom: "14px",
          }}
        >
          {notice}
        </div>
      )}

      {!token ? (
        <div
          style={{
            border: `1px dashed ${C.border}`,
            borderRadius: "12px",
            padding: "14px 16px",
            textAlign: "center",
            fontSize: "13px",
            color: C.muted,
            marginBottom: "16px",
          }}
        >
          <button
            onClick={() => setShowAuth(true)}
            style={{
              border: "none",
              background: "transparent",
              color: C.link,
              fontWeight: 600,
              cursor: "pointer",
              padding: "0",
            }}
          >
            Log in
          </button>{" "}
          to join the conversation.
        </div>
      ) : (
        <div style={{ display: "flex", gap: "10px", alignItems: "flex-start", marginBottom: "16px" }}>
          <input
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                submit(newText, null);
              }
            }}
            placeholder="Add a comment…"
            maxLength={1000}
            style={{
              flex: 1,
              border: `1px solid ${C.border}`,
              borderRadius: "12px",
              padding: "12px 14px",
              fontSize: "14px",
              outline: "none",
            }}
          />
          <button
            onClick={() => submit(newText, null)}
            disabled={busy || !newText.trim()}
            style={{
              border: "none",
              borderRadius: "12px",
              background: "linear-gradient(135deg, #f59e0b 0%, #f97316 100%)",
              color: "#fff",
              padding: "12px 18px",
              fontSize: "14px",
              fontWeight: 700,
              cursor: busy || !newText.trim() ? "default" : "pointer",
              opacity: busy || !newText.trim() ? 0.6 : 1,
            }}
          >
            Post
          </button>
        </div>
      )}

      {loading ? (
        <div style={{ fontSize: "13px", color: "#9ca3af", padding: "12px 0" }}>Loading comments…</div>
      ) : error ? (
        <div style={{ fontSize: "13px", color: "#b91c1c" }}>{error}</div>
      ) : comments.length === 0 ? (
        <div style={{ fontSize: "13px", color: "#9ca3af", padding: "12px 0" }}>
          No comments yet — be the first to share your thoughts.
        </div>
      ) : (
        <div>{comments.map((c) => renderComment(c, false))}</div>
      )}
    </>
  );

  // Mobile: comments open in a scrollable bottom sheet, triggered via the
  // imperative openSheet handle (the comment icon on the Look page).
  if (isMobile) {
    return (
      <>
        {sheetOpen && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(2,6,23,0.55)",
              zIndex: 3000,
              display: "flex",
              alignItems: "flex-end",
            }}
            onClick={() => setSheetOpen(false)}
          >
            <div
              style={{
                width: "100%",
                maxHeight: "82vh",
                background: "#fff",
                borderTopLeftRadius: "18px",
                borderTopRightRadius: "18px",
                display: "flex",
                flexDirection: "column",
                boxShadow: "0 -12px 40px rgba(2,6,23,0.35)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "14px 16px 8px",
                  borderBottom: `1px solid ${C.border}`,
                  flexShrink: 0,
                }}
              >
                <span style={{ fontSize: "15px", fontWeight: 700, color: C.text }}>
                  Comments {totalCount > 0 ? `(${totalCount})` : ""}
                </span>
                <button
                  onClick={() => setSheetOpen(false)}
                  aria-label="Close comments"
                  style={{
                    width: "32px",
                    height: "32px",
                    borderRadius: "50%",
                    border: "none",
                    background: "#f1f5f9",
                    color: C.text,
                    fontSize: "18px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  ×
                </button>
              </div>
              <div
                style={{
                  overflowY: "auto",
                  WebkitOverflowScrolling: "touch",
                  padding: "14px 16px 20px",
                  flex: 1,
                }}
              >
                {renderThread()}
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <section style={{ marginTop: "36px", ...containerStyle }}>
      <h2
        style={{
          margin: "0 0 4px 0",
          fontSize: "18px",
          fontWeight: 700,
          color: C.text,
        }}
      >
        <FaRegComment style={{ marginRight: "8px", color: C.faint }} />
        Comments {totalCount > 0 ? `(${totalCount})` : ""}
      </h2>
      <p style={{ margin: "0 0 16px 0", fontSize: "12px", color: "#9ca3af" }}>
        Be kind — comments are public and moderated.
      </p>
      {renderThread()}
      <AuthRequiredModal
        open={showAuth}
        onClose={() => setShowAuth(false)}
        intent="comment on this look"
        onAuthed={() => {
          setShowAuth(false);
          setAuthTick((t) => t + 1);
          load();
        }}
      />
    </section>
  );
});

export default CommentsSection;
